import React from "react";
import { Link2, AlertOctagon, Sparkles, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ProgressRing } from "../ProgressRing";
import { useInsights } from "../../data/insightsStore";

const linkTone = {
  healthy: {
    ring: "border-indigo-300/70 bg-indigo-50/40",
    dot: "bg-indigo-500",
    chip: "bg-indigo-50 text-indigo-700 border-indigo-200",
    label: "Healthy",
  },
  watch: {
    ring: "border-amber-300/70 bg-amber-50/40",
    dot: "bg-amber-500",
    chip: "bg-amber-50 text-amber-700 border-amber-200",
    label: "Watch",
  },
  broken: {
    ring: "border-rose-300/80 bg-rose-50/60",
    dot: "bg-rose-500",
    chip: "bg-rose-50 text-rose-700 border-rose-200",
    label: "Critical",
  },
};

export const ChainVisualization = () => {
  const navigate = useNavigate();
  const { data } = useInsights();
  const viz = data?.chain_viz;
  if (!viz) return null;
  const { links = [], completion_probability: prob = 0, weakest_note: note, title, deal_id } = viz;
  if (!links.length) return null;

  const ringColor = prob >= 80 ? "#6366F1" : prob >= 60 ? "#F59E0B" : "#F43F5E";
  // Find the index of the weakest link to overlay the "Weak" segment
  const weakIdx = links.reduce((minI, l, i, arr) => (l.score < arr[minI].score ? i : minI), 0);
  const cellWidth = `${100 / links.length}%`;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white cf-shadow overflow-hidden" data-testid="chain-visualization">
      <div className="relative px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-slate-900 text-indigo-300 flex items-center justify-center"><Link2 size={15} /></div>
          <div>
            <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Chain map</p>
            <h2 className="font-display text-[18px] font-bold text-slate-900" data-testid="chain-title">{title}</h2>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ProgressRing value={prob} size={56} stroke={6} color={ringColor} label={`${prob}`} />
          <div>
            <p className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Completion probability</p>
            <p className="font-display text-[15px] font-bold text-slate-900">{links.length}-link chain · live</p>
          </div>
        </div>
      </div>

      <div className="px-6 pt-6 pb-4 overflow-x-auto cf-scroll">
        <div className="relative min-w-[920px]">
          <div className="absolute top-[34px] left-[40px] right-[40px] h-[3px] rounded-full bg-gradient-to-r from-indigo-400 via-indigo-400 to-indigo-300" />
          {/* Weak segment overlay centred on weakest link */}
          <div
            className="absolute top-[34px] h-[3px] rounded-full bg-rose-500"
            style={{ left: `calc(${(weakIdx / links.length) * 100}% + 8px)`, width: `calc(${100 / links.length}% - 16px)` }}
          />
          <div className="absolute top-[28px] h-[15px] flex items-center justify-center" style={{ left: `calc(${((weakIdx + 0.5) / links.length) * 100}% - 18px)` }}>
            <span className="px-1.5 py-0.5 rounded-md bg-rose-500 text-white text-[9px] font-bold uppercase tracking-[0.16em]">Weak</span>
          </div>

          <div className="grid relative" style={{ gridTemplateColumns: `repeat(${links.length}, ${cellWidth})`, gap: "1rem" }}>
            {links.map((l) => {
              const t = linkTone[l.status] || linkTone.watch;
              return (
                <div key={l.id} className="flex flex-col items-center text-center" data-testid={`chain-link-${l.id}`}>
                  <div className={`w-[68px] h-[68px] rounded-2xl border-2 ${t.ring} flex items-center justify-center bg-white relative`}>
                    <span className="font-display text-[18px] font-bold text-slate-900">{l.score}</span>
                    {l.status === "broken" && (
                      <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-500 text-white flex items-center justify-center cf-pulse">
                        <AlertOctagon size={11} />
                      </span>
                    )}
                  </div>
                  <p className="mt-3 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{l.role}</p>
                  <p className="text-[12.5px] font-semibold text-slate-900 mt-0.5 leading-tight">{l.name}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{l.value}{l.tag ? ` · ${l.tag}` : ""}</p>
                  <span className={`mt-2 inline-flex items-center gap-1 text-[9.5px] font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full border ${t.chip}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} /> {t.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="px-5 pb-5">
        <div className="flex items-start gap-3 px-4 py-3.5 rounded-xl bg-slate-900 text-white">
          <Sparkles size={14} className="text-indigo-300 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10.5px] uppercase tracking-[0.18em] text-indigo-300 font-bold">Structural insight</p>
            <p className="text-[12.5px] text-slate-200 mt-1 leading-relaxed">{note}</p>
          </div>
          {deal_id && (
            <button
              data-testid="chain-open-deal"
              onClick={() => navigate(`/deal-rooms/${deal_id}`)}
              className="hidden md:inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white text-[11.5px] font-semibold transition-colors"
            >
              Open chain <ArrowRight size={12} />
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
